/**
 * R-72 — Output distribution sanity vs corpus baseline.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import {
  computeStats,
  DISTRIBUTION_STAT_FIELDS,
  DISTRIBUTION_STAT_LABELS,
  type DistributionStats,
} from './distributionStats';
import type { DistributionBaseline } from './distributionBaselineBuilder';

export interface DistributionFinding {
  stat: keyof DistributionStats;
  statLabel: string;
  observed: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  severity: 'block' | 'warn';
  message: string;
  suggestion: string;
}

export interface DistributionCheckMeta {
  reportTypeId: ReportTypeId;
  corpusSampleSize: number;
  insufficientCorpus: boolean;
  minSample: number;
  checkedAt: string;
}

const HARD_MIN_WORDS = 500;
const HARD_MAX_WORDS = 25000;

/**
 * FIX 1 — report types whose OUTPUT SHAPE the corpus percentile baseline does
 * NOT model, so the percentile comparison produces false positives.
 *
 * `custom-brief` is a user-defined Q&A diligence brief: its body is an arbitrary
 * set of `questionSections` / `customBriefAnswers`, NOT the standard multi-section
 * report structure the per-type baselines are built from (today every type's
 * baseline is the SAME feasibility-shaped seed — 2,500-word p10, 8-citation p10,
 * 4-section p10). A legitimately focused brief (a handful of questions) has far
 * fewer words / sections / `[Ref N]` citations than that p10, so `checkPercentiles`
 * block-flags it as "likely incomplete" → `resolveQualityHold` stamps a
 * needs-revision qualityHold on a report the reviewer PASSED (the observed
 * false positive).
 *
 * For these types we run ONLY the type-agnostic HARD FLOORS (a genuinely empty
 * `< 500`-word or runaway `> 25,000`-word body still blocks) and SKIP the
 * percentile comparison. Report types the baseline DOES model (feasibility and
 * the other standard-section types) are unchanged — genuine distribution holds
 * still fire. Scoped deliberately narrow: only `custom-brief` has a free-form,
 * user-defined body shape; the other `usesScoring:false` types (test-fit,
 * public-sentiment, executive-summary) emit standard section sets the baseline
 * models closely enough that a truncated one SHOULD still hold.
 */
export const DISTRIBUTION_PERCENTILE_UNMODELED_TYPES: readonly ReportTypeId[] = [
  'custom-brief',
];

function suggestionFor(
  stat: keyof DistributionStats,
  observed: number,
  p10: number,
  p90: number,
  severity: 'block' | 'warn',
  direction: 'low' | 'high',
): string {
  const label = DISTRIBUTION_STAT_LABELS[stat];
  if (severity === 'block') {
    if (stat === 'wordCount' && observed < HARD_MIN_WORDS) {
      return 'Report body is too short — regenerate or expand sections before publishing.';
    }
    if (stat === 'wordCount' && observed > HARD_MAX_WORDS) {
      return 'Report body exceeds maximum length — trim duplication or split content.';
    }
    if (direction === 'low') {
      return `${label} is far below corpus baseline (observed ${observed}, p10 ${p10}) — likely incomplete generation.`;
    }
    return `${label} is far above corpus baseline (observed ${observed}, p90 ${p90}) — check for duplication or runaway generation.`;
  }
  if (direction === 'low') {
    return `${label} is unusually low vs corpus — verify section completeness and citation coverage.`;
  }
  return `${label} is unusually high vs corpus — verify conciseness and avoid redundant tables/citations.`;
}

function checkHardFloors(stats: DistributionStats): DistributionFinding[] {
  const findings: DistributionFinding[] = [];
  if (stats.wordCount < HARD_MIN_WORDS) {
    findings.push({
      stat: 'wordCount',
      statLabel: DISTRIBUTION_STAT_LABELS.wordCount,
      observed: stats.wordCount,
      p10: HARD_MIN_WORDS,
      p25: HARD_MIN_WORDS,
      p50: HARD_MIN_WORDS,
      p75: HARD_MIN_WORDS,
      p90: HARD_MIN_WORDS,
      severity: 'block',
      message: `Word count ${stats.wordCount} is below hard minimum (${HARD_MIN_WORDS}).`,
      suggestion: suggestionFor('wordCount', stats.wordCount, HARD_MIN_WORDS, HARD_MAX_WORDS, 'block', 'low'),
    });
  }
  if (stats.wordCount > HARD_MAX_WORDS) {
    findings.push({
      stat: 'wordCount',
      statLabel: DISTRIBUTION_STAT_LABELS.wordCount,
      observed: stats.wordCount,
      p10: HARD_MAX_WORDS,
      p25: HARD_MAX_WORDS,
      p50: HARD_MAX_WORDS,
      p75: HARD_MAX_WORDS,
      p90: HARD_MAX_WORDS,
      severity: 'block',
      message: `Word count ${stats.wordCount} exceeds hard maximum (${HARD_MAX_WORDS}).`,
      suggestion: suggestionFor('wordCount', stats.wordCount, HARD_MIN_WORDS, HARD_MAX_WORDS, 'block', 'high'),
    });
  }
  return findings;
}

function checkPercentiles(
  stats: DistributionStats,
  baseline: DistributionBaseline,
): DistributionFinding[] {
  const findings: DistributionFinding[] = [];
  for (const field of DISTRIBUTION_STAT_FIELDS) {
    if (field === 'wordCount') continue;
    const observed = stats[field];
    const pct = baseline.stats[field];
    const { p10, p25, p50, p75, p90 } = pct;

    let severity: 'block' | 'warn' | null = null;
    let direction: 'low' | 'high' = 'low';
    if (observed < p10 / 2 || observed > p90 * 2) {
      severity = 'block';
      direction = observed < p10 / 2 ? 'low' : 'high';
    } else if (observed < p10 || observed > p90) {
      severity = 'warn';
      direction = observed < p10 ? 'low' : 'high';
    }
    if (!severity) continue;

    const statLabel = DISTRIBUTION_STAT_LABELS[field];
    const range = `${p10}–${p90}`;
    findings.push({
      stat: field,
      statLabel,
      observed,
      p10,
      p25,
      p50,
      p75,
      p90,
      severity,
      message: `${statLabel} ${observed} is outside corpus p10–p90 range (${range}).`,
      suggestion: suggestionFor(field, observed, p10, p90, severity, direction),
    });
  }

  const wordObserved = stats.wordCount;
  const wordPct = baseline.stats.wordCount;
  const { p10, p25, p50, p75, p90 } = wordPct;
  if (wordObserved >= HARD_MIN_WORDS && wordObserved <= HARD_MAX_WORDS) {
    let severity: 'block' | 'warn' | null = null;
    let direction: 'low' | 'high' = 'low';
    if (wordObserved < p10 / 2 || wordObserved > p90 * 2) {
      severity = 'block';
      direction = wordObserved < p10 / 2 ? 'low' : 'high';
    } else if (wordObserved < p10 || wordObserved > p90) {
      severity = 'warn';
      direction = wordObserved < p10 ? 'low' : 'high';
    }
    if (severity) {
      findings.push({
        stat: 'wordCount',
        statLabel: DISTRIBUTION_STAT_LABELS.wordCount,
        observed: wordObserved,
        p10,
        p25,
        p50,
        p75,
        p90,
        severity,
        message: `Word count ${wordObserved} is outside corpus p10–p90 range (${p10}–${p90}).`,
        suggestion: suggestionFor('wordCount', wordObserved, p10, p90, severity, direction),
      });
    }
  }

  return findings;
}

export function getDistributionCheckMeta(
  reportTypeId: ReportTypeId,
  baseline: DistributionBaseline,
): DistributionCheckMeta {
  const minSample = AUDIT_CONFIG.DISTRIBUTION_MIN_SAMPLE;
  return {
    reportTypeId,
    corpusSampleSize: baseline.reportCount,
    insufficientCorpus: baseline.reportCount < minSample,
    minSample,
    checkedAt: new Date().toISOString(),
  };
}

export function checkDistributionSanity(
  report: SiteRagReport,
  baseline: DistributionBaseline,
): DistributionFinding[] {
  const stats = computeStats(report);
  const hardFindings = checkHardFloors(stats);
  // FIX 1 — a report type the percentile baseline does not model (custom-brief's
  // free-form Q&A shape) gets HARD FLOORS ONLY. The feasibility-shaped percentile
  // comparison would false-flag its legitimately narrow body as incomplete and
  // trip a needs-revision qualityHold despite a clean reviewer pass.
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;
  if (DISTRIBUTION_PERCENTILE_UNMODELED_TYPES.includes(reportTypeId)) {
    return hardFindings;
  }
  if (baseline.reportCount < AUDIT_CONFIG.DISTRIBUTION_MIN_SAMPLE) {
    return hardFindings;
  }
  return [...hardFindings, ...checkPercentiles(stats, baseline)];
}

export function applyDistributionSanityGate(
  report: SiteRagReport,
  baseline: DistributionBaseline,
): SiteRagReport {
  const reportTypeId = (report.reportType ?? 'feasibility') as ReportTypeId;
  const distribution = checkDistributionSanity(report, baseline);
  const distributionCheckMeta = getDistributionCheckMeta(reportTypeId, baseline);
  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      distribution,
      distributionCheckMeta,
    },
  };
}
